import React, { useState, useEffect } from 'react';
import { 
  FileText, Info, Briefcase, User, ShieldAlert, CreditCard, 
  AlertCircle, Award, Scale, Lock, Activity, Layers, Gavel, 
  RefreshCw, Mail, Printer, ArrowUp, MessageCircle, Globe, ShieldCheck, ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsAndConditions = () => {
  const [activeSection, setActiveSection] = useState('introduction');
  const navigate = useNavigate();

  // ScrollSpy logic to highlight active section in sidebar
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      let currentSection = 'introduction';
      
      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        if (window.scrollY >= sectionTop - 150) {
          currentSection = section.getAttribute('id');
        }
      });
      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Google Font Import */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        
        .font-serif {
          font-family: 'Playfair Display', serif;
        }
        
        .font-inter {
          font-family: 'Inter', sans-serif;
        }

        /* Hide elements during print */
        @media print {
          .no-print { display: none !important; }
          .print-full-width { width: 100% !important; max-width: 100% !important; }
          body { background-color: white !important; }
          .shadow-sm, .shadow-md, .shadow-xl { box-shadow: none !important; border: 1px solid #e2e8f0 !important; }
        }
      `}</style>

      <main className="pt-[120px] pb-20 bg-[#f8fafc] min-h-screen font-inter">
        <div className="max-w-[1250px] mx-auto px-5 flex flex-col lg:flex-row gap-10 items-start">
          
          {/* Left Sidebar (Sticky) */}
          <aside className="w-[260px] shrink-0 hidden lg:block sticky top-[120px] no-print">
            <span className="block text-[11px] font-black text-slate-400 uppercase tracking-[0.15em] mb-5 pl-3">
              Contents
            </span>
            <nav className="flex flex-col space-y-1">
              {[
                { id: 'introduction', label: 'Introduction' },
                { id: 'services-offered', label: 'Services Offered' },
                { id: 'eligibility', label: 'Eligibility' },
                { id: 'user-responsibilities', label: 'User Responsibilities' },
                { id: 'fees-payments', label: 'Internship Fees & Payments' },
                { id: 'refund-policy', label: 'Refund Policy' },
                { id: 'certificates', label: 'Certificates & Deliverables' },
                { id: 'intellectual-property', label: 'Intellectual Property' },
                { id: 'confidentiality', label: 'Confidentiality' },
                { id: 'limitation-of-liability', label: 'Limitation of Liability' },
                { id: 'third-party-tools', label: 'Third-Party Tools' },
                { id: 'governing-law', label: 'Governing Law' },
                { id: 'updates-to-terms', label: 'Updates to Terms' },
                { id: 'contact', label: 'Contact' },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-left px-3 py-2 text-[14px] rounded-lg transition-all ${
                    activeSection === item.id 
                      ? 'text-[#0284c7] font-bold bg-[#f0f9ff]' 
                      : 'text-slate-500 font-medium hover:text-[#0f172a] hover:bg-slate-100'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <button 
              onClick={handlePrint}
              className="mt-10 w-full py-3 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold text-[13px] rounded-xl flex items-center justify-center gap-2 transition-colors shadow-sm"
            >
              <Printer size={16} /> Print Document
            </button>
          </aside>

          {/* Main Content Area */}
          <div className="flex-1 w-full bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden print-full-width">
            
            {/* Header / Hero Area inside the card */}
            <div className="p-8 md:p-12 border-b border-slate-100 relative">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-8">
                <div className="w-16 h-16 bg-[#0284c7] rounded-[18px] flex items-center justify-center shadow-lg shadow-sky-500/20 shrink-0">
                  <FileText size={32} className="text-white" strokeWidth={2} />
                </div>
                <div>
                  <h1 className="text-[36px] md:text-[42px] font-serif text-[#0f172a] font-normal leading-tight mb-3 tracking-tight">
                    Terms & Conditions
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 text-[13px]">
                    <span className="bg-[#e0f2fe] text-[#0284c7] px-3 py-1 rounded-md font-bold uppercase tracking-wider text-[10px]">
                      Official Policy
                    </span>
                    <span className="text-slate-400 font-medium">
                      Updated: February 27, 2026
                    </span>
                  </div>
                </div>
              </div>

              {/* Main Info Box */}
              <div className="bg-[#f0f9ff] border border-[#e0f2fe] rounded-2xl p-5 flex items-start gap-4">
                <Info className="text-[#0284c7] shrink-0 mt-0.5" size={20} />
                <p className="text-[#334155] text-[15px] font-medium leading-relaxed">
                  This page explains your rights, payments, and responsibilities clearly to ensure transparency and trust. We believe in transparent policies and fair usage.
                </p>
              </div>
            </div>

            {/* Content Sections */}
            <div className="p-8 md:p-12 space-y-12 md:space-y-16">
              
              {/* Introduction */}
              <section id="introduction" className="scroll-mt-32">
                <SectionHeader icon={<Info size={18} />} title="Introduction" />
                <p className="text-slate-600 text-[15px] leading-relaxed">
                  By using SENVRA AI's website, internships, training programs, or services, you agree to these terms and conditions.
                </p>
              </section>

              {/* Services Offered */}
              <section id="services-offered" className="scroll-mt-32">
                <SectionHeader icon={<Briefcase size={18} />} title="Services Offered" />
                <p className="text-slate-600 text-[15px] leading-relaxed mb-4">
                  SENVRA AI provides a wide range of digital and educational services:
                </p>
                <ul className="space-y-2 text-slate-600 text-[15px] leading-relaxed ml-2">
                  <li><strong className="text-slate-800">Internships and training programs</strong> for students and professionals</li>
                  <li><strong className="text-slate-800">Software and app development</strong> services for businesses</li>
                  <li><strong className="text-slate-800">AI automation solutions</strong> and digital transformation</li>
                  <li><strong className="text-slate-800">Digital products and tools</strong> for developers</li>
                  <li><strong className="text-slate-800">Hackathons, workshops, and educational programs</strong></li>
                </ul>
                <p className="text-slate-600 text-[15px] leading-relaxed mt-4">
                  Please note that services may change or be updated anytime to ensure the best experience.
                </p>
              </section>

              {/* Eligibility */}
              <section id="eligibility" className="scroll-mt-32">
                <SectionHeader icon={<User size={18} />} title="Eligibility" />
                <p className="text-slate-600 text-[15px] leading-relaxed">
                  Users must provide correct information and use the platform honestly. You are responsible for maintaining the confidentiality of any account details.
                </p>
              </section>

              {/* User Responsibilities */}
              <section id="user-responsibilities" className="scroll-mt-32">
                <SectionHeader icon={<ShieldAlert size={18} />} title="User Responsibilities" />
                <p className="text-slate-600 text-[15px] leading-relaxed mb-4">
                  To maintain a fair and safe environment, users must:
                </p>
                <ul className="space-y-2 text-slate-600 text-[15px] leading-relaxed ml-2 mb-6">
                  <li><strong className="text-slate-800">Avoid plagiarism or cheating</strong> in any submitted tasks</li>
                  <li>Use all services legally and ethically</li>
                  <li>Not misuse or redistribute platform content</li>
                  <li>Not attempt hacking, copying, or reverse-engineering</li>
                </ul>
                <p className="text-[#ef4444] font-medium text-[15px]">
                  Violation of these responsibilities may lead to immediate suspension or termination of services.
                </p>
              </section>

              {/* Internship Fees & Payments */}
              <section id="fees-payments" className="scroll-mt-32">
                <SectionHeader icon={<CreditCard size={18} />} title="Internship Fees & Payments" />
                <p className="text-slate-600 text-[15px] leading-relaxed mb-6">
                  Internship participation requires a small <strong className="text-slate-800">task submission fee</strong> to cover administrative costs:
                </p>
                
                {/* Specific UI for Pricing Box matching screenshot */}
                <div className="bg-[#f0f9ff] border border-[#e0f2fe] rounded-[20px] p-6 mb-6">
                  <div className="flex items-center gap-2 text-[#0369a1] font-serif font-bold text-[17px] mb-5">
                    <CreditCard size={18} /> Internship Task Submission Fee
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                    <div className="bg-white border border-[#e0f2fe] rounded-[14px] p-5 shadow-sm">
                      <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-1">1 Month Internship</p>
                      <p className="text-[32px] font-bold text-slate-800">₹99</p>
                    </div>
                    <div className="bg-white border border-[#e0f2fe] rounded-[14px] p-5 shadow-sm">
                      <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-1">3 Month Internship</p>
                      <p className="text-[32px] font-bold text-slate-800">₹199</p>
                    </div>
                  </div>
                  
                  <p className="text-[14px] italic text-slate-500 font-medium">
                    "These are task review charges, <strong className="text-slate-700">not hidden fees</strong>, and help maintain platform operations, servers, and mentorship support."
                  </p>
                </div>

                <p className="text-slate-600 text-[15px] leading-relaxed">
                  These charges help support servers, mentors, and platform operations. They are charged only for managing and reviewing tasks. No personal profit or unnecessary fees are collected.
                </p>
              </section>

              {/* Refund Policy */}
              <section id="refund-policy" className="scroll-mt-32">
                <SectionHeader icon={<AlertCircle size={18} />} title="Refund Policy" />
                
                {/* Orange Alert Box */}
                <div className="bg-[#fff7ed] border border-[#ffedd5] rounded-xl p-4 flex items-center gap-3 mb-6">
                  <AlertCircle className="text-[#ea580c] shrink-0" size={20} />
                  <p className="text-[#9a3412] text-[14.5px] font-medium">
                    All payments are <strong className="text-[#7c2d12]">non-refundable</strong> unless SENVRA AI cancels the program itself.
                  </p>
                </div>

                <p className="text-slate-600 text-[15px] leading-relaxed">
                  Please ensure you are committed to the program before making any payment. Refunds are issued only in the rare event that SENVRA AI cancels the specific program you enrolled in.
                </p>
              </section>

              {/* Certificates & Deliverables */}
              <section id="certificates" className="scroll-mt-32">
                <SectionHeader icon={<Award size={18} />} title="Certificates & Deliverables" />
                <p className="text-slate-600 text-[15px] leading-relaxed">
                  Certificates are issued only after <strong className="text-slate-800">successful completion of tasks</strong> and a positive performance evaluation by our mentors.
                </p>
              </section>

              {/* Intellectual Property */}
              <section id="intellectual-property" className="scroll-mt-32">
                <SectionHeader icon={<Scale size={18} />} title="Intellectual Property" />
                <p className="text-slate-600 text-[15px] leading-relaxed">
                  All content, code, branding, and materials belong to SENVRA AI and cannot be copied, resold, or redistributed without explicit written permission.
                </p>
              </section>

              {/* Confidentiality */}
              <section id="confidentiality" className="scroll-mt-32">
                <SectionHeader icon={<Lock size={18} />} title="Confidentiality" />
                <p className="text-slate-600 text-[15px] leading-relaxed">
                  Any project data or client information shared with SENVRA AI is treated with strict confidentiality and is protected by our internal security protocols.
                </p>
              </section>

              {/* Limitation of Liability */}
              <section id="limitation-of-liability" className="scroll-mt-32">
                <SectionHeader icon={<Activity size={18} />} title="Limitation of Liability" />
                <p className="text-slate-600 text-[15px] leading-relaxed mb-4">
                  SENVRA AI is not responsible for:
                </p>
                <ul className="space-y-2 text-slate-600 text-[15px] leading-relaxed ml-2">
                  <li>Internet or technical issues on the user's end</li>
                  <li>Data loss due to user mistakes or negligence</li>
                  <li>Third-party payment failures or delays</li>
                  <li>External tool downtime or API failures</li>
                </ul>
              </section>

              {/* Third-Party Tools */}
              <section id="third-party-tools" className="scroll-mt-32">
                <SectionHeader icon={<Layers size={18} />} title="Third-Party Tools" />
                <p className="text-slate-600 text-[15px] leading-relaxed">
                  Some services may use payment gateways, APIs, or hosting providers beyond SENVRA AI's control. Usage of these tools is subject to their respective terms.
                </p>
              </section>

              {/* Governing Law */}
              <section id="governing-law" className="scroll-mt-32">
                <SectionHeader icon={<Gavel size={18} />} title="Governing Law" />
                <p className="text-slate-600 text-[15px] leading-relaxed">
                  These terms are governed by and construed in accordance with the <strong className="text-slate-800">laws of India</strong>.
                </p>
              </section>

              {/* Updates to Terms */}
              <section id="updates-to-terms" className="scroll-mt-32">
                <SectionHeader icon={<RefreshCw size={18} />} title="Updates to Terms" />
                <p className="text-slate-600 text-[15px] leading-relaxed">
                  Terms may change anytime to reflect changes in our services or legal requirements. Continued use of the platform means acceptance of the updated terms.
                </p>
              </section>

              {/* Contact */}
              <section id="contact" className="scroll-mt-32">
                <SectionHeader icon={<Mail size={18} />} title="Contact" />
                <p className="text-slate-600 text-[15px] leading-relaxed mb-5">
                  If you have any questions, feel free to reach out:
                </p>
                <div className="space-y-3">
                  <a href="mailto:contact@senvra.ai" className="flex items-center gap-3 text-[#0284c7] font-medium text-[15px] hover:underline">
                    <Mail size={18} /> contact@senvra.ai
                  </a>
                  <a href="https://www.senvraai.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-[#0284c7] font-medium text-[15px] hover:underline">
                    <Globe size={18} /> www.senvraai.com
                  </a>
                </div>
              </section>

              {/* Bottom CTA Card */}
              <div className="mt-16 bg-[#f8fafc] border border-slate-200 rounded-[24px] p-10 text-center relative overflow-hidden">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center border border-slate-200 shadow-sm mx-auto mb-5 text-[#0284c7]">
                  <MessageCircle size={22} />
                </div>
                <h3 className="text-[26px] md:text-[28px] font-serif text-[#0f172a] font-bold mb-3 tracking-tight">
                  Still have questions?
                </h3>
                <p className="text-slate-500 text-[15px] mb-8 font-medium">
                  Our team is happy to help clarify anything regarding our policies or services.
                </p>
                <button 
                  onClick={() => navigate('/contact')}
                  className="bg-[#0284c7] hover:bg-[#0369a1] text-white px-8 py-3.5 rounded-xl font-bold text-[14.5px] transition-all flex items-center justify-center gap-2 mx-auto shadow-md"
                >
                  Contact Support <ExternalLink size={16} />
                </button>

                {/* Footer of the CTA Card */}
                <div className="mt-12 pt-6 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                  <div className="flex gap-4">
                    <span>Terms</span>
                    <span>•</span>
                    <span>Privacy</span>
                    <span>•</span>
                    <span>Cookies</span>
                  </div>
                  <div className="flex items-center gap-2 normal-case tracking-normal font-medium text-slate-500 text-[13px]">
                    <ShieldCheck size={16} className="text-emerald-500" /> We believe in transparent policies and fair usage.
                  </div>
                  <button 
                    onClick={scrollToTop}
                    className="flex items-center gap-1 text-[#0284c7] hover:text-[#0369a1] transition-colors"
                  >
                    Back to Top <ArrowUp size={14} />
                  </button>
                </div>
              </div>

            </div>
          </div>
          
        </div>

        {/* Floating Back to top for mobile */}
        <button 
          onClick={scrollToTop}
          className="lg:hidden fixed bottom-6 right-6 w-12 h-12 bg-white rounded-full shadow-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:text-[#0284c7] hover:border-[#0284c7] transition-colors z-50"
        >
          <ArrowUp size={20} />
        </button>

      </main>
    </>
  );
};

// Reusable Section Header Component
const SectionHeader = ({ icon, title }) => (
  <div className="flex items-center gap-4 mb-5">
    <div className="w-10 h-10 rounded-[10px] bg-[#f0f9ff] flex items-center justify-center text-[#0284c7] shrink-0 border border-[#e0f2fe]">
      {icon}
    </div>
    <h3 className="font-serif text-[22px] md:text-[24px] text-[#0f172a] font-normal tracking-tight">
      {title}
    </h3>
  </div>
);

export default TermsAndConditions;